/** One tab-local observer, installed by the beacon. Bundle consumers use only the window bridge. */
export type ActionLane = 'spell' | 'attack' | 'walk' | 'end-turn' | 'dodge';
type Phase = 'gate_wait_ms' | 'decide_ms' | 'apply_ms' | 'paint_ms';
export type SlowTiming = { kind: 'slow'; reason: string; client_ms: number; detail: Record<string, number> };
interface Timing {
    key?: string;
    lane: ActionLane; started: number; at: number; phase: Phase; phases: Record<Phase, number>;
    operation?: string; painted?: number; commitAt?: number; commitMs?: number; echoAt?: number; echoMs: number; compileMs: number;
    timeout?: ReturnType<typeof setTimeout>;
}
export function createActionTiming(options: {
    now(): number; post(event: SlowTiming): void; afterPaint(callback: () => void): void;
    later(callback: () => void, ms: number): ReturnType<typeof setTimeout>; cancel(timer: ReturnType<typeof setTimeout>): void;
}) {
    const active = new Map<string, Timing>(), keys = new Map<string, Timing>(), operations = new Map<string, Timing>(), last = new Map<ActionLane, number>();
    const intents = new Map<number, number>();
    let sequence = 0, inputAt: number | undefined;
    const drop = (id: string, timing: Timing): void => {
        active.delete(id);
        if (timing.key && keys.get(timing.key) === timing) keys.delete(timing.key);
        if (timing.operation && operations.get(timing.operation) === timing) operations.delete(timing.operation);
        if (timing.timeout !== undefined) try { options.cancel(timing.timeout); } catch { /* Observer only. */ }
    };
    const send = (id: string, timing: Timing): void => {
        if (timing.painted === undefined || !active.has(id)) return;
        const now = options.now(), previous = last.get(timing.lane);
        if (previous !== undefined && now - previous < 5000) { drop(id, timing); return; }
        const detail: Record<string, number> = { ...timing.phases, echo_apply_ms: Math.round(timing.echoMs), compile_ms: Math.round(timing.compileMs) };
        if (timing.commitMs !== undefined) detail.commit_rtt_ms = Math.round(timing.commitMs);
        else if (timing.operation) detail.commit_pending = 1;
        drop(id, timing);
        last.set(timing.lane, now);
        try { options.post({ kind: 'slow', reason: 'action:' + timing.lane, client_ms: timing.painted, detail }); } catch { /* Observer only. */ }
    };
    const find = (id: string | undefined): Timing | undefined => id === undefined ? undefined : active.get(id) ?? keys.get(id) ?? operations.get(id);
    const mark = (id: string | undefined, phase: Phase): void => {
        const timing = find(id);
        if (!timing || timing.painted !== undefined) return;
        const at = Math.max(timing.at, options.now());
        // Round cumulative boundaries, rather than each interval, so the four phases sum exactly to client_ms.
        timing.phases[timing.phase] += Math.round(at - timing.started) - Math.round(timing.at - timing.started);
        timing.at = at; timing.phase = phase;
        if (phase === 'apply_ms' && inputAt === timing.started) inputAt = undefined;
    };
    const api = {
        input(): void {
            const at = inputAt = options.now();
            // An unrelated inspection click must not lengthen a later automatic action.
            options.later(() => { if (inputAt === at) inputAt = undefined; }, 0);
        },
        intent(): () => void {
            const id = ++sequence, inherited = [...intents.values()].pop();
            intents.set(id, inputAt ?? inherited ?? options.now());
            return () => { intents.delete(id); };
        },
        start(lane: ActionLane, key?: string): string | undefined {
            if (active.size >= 32) return undefined;
            const id = 'action-timing-' + ++sequence;
            const now = options.now(), candidate = [...intents.values()].pop() ?? inputAt;
            const started = candidate !== undefined && now - candidate >= 0 ? candidate : now;
            const timing: Timing = { key, lane, started, at: started, phase: 'gate_wait_ms',
                phases: { gate_wait_ms: 0, decide_ms: 0, apply_ms: 0, paint_ms: 0 }, echoMs: 0, compileMs: 0 };
            active.set(id, timing);
            if (key) keys.set(key, timing);
            return id;
        },
        mark,
        bind(id: string | undefined, operation: string): void {
            const timing = find(id);
            if (!timing) return;
            if (timing.operation) operations.delete(timing.operation);
            timing.operation = operation; operations.set(operation, timing);
        },
        end(id: string | undefined, discard = false): void {
            const timing = id === undefined ? undefined : active.get(id);
            if (!timing || timing.painted !== undefined) return;
            if (discard) { drop(id!, timing); return; }
            if (inputAt === timing.started) inputAt = undefined;
            mark(id, 'paint_ms');
            if (timing.key && keys.get(timing.key) === timing) keys.delete(timing.key);
            options.afterPaint(() => { try {
                if (!active.has(id!)) return;
                mark(id, 'paint_ms');
                const ms = Math.round(timing.at - timing.started), previous = last.get(timing.lane);
                if (ms <= 250 || (previous !== undefined && timing.at - previous < 5000)) { drop(id!, timing); return; }
                timing.painted = ms;
                // Delivery never extends client_ms. Wait off-path for its diagnostics, with a bounded missing-answer fallback.
                if (!timing.operation || timing.commitMs !== undefined) send(id!, timing);
                else timing.timeout = options.later(() => { try { send(id!, timing); } catch { /* Observer only. */ } }, 20_000);
            } catch { drop(id!, timing); } });
        },
        commit(operation: string, outcome: string): void {
            const timing = operations.get(operation);
            if (!timing) return;
            if (outcome === 'sending') timing.commitAt ??= options.now();
            else if (timing.commitAt !== undefined) {
                timing.commitMs = Math.max(0, options.now() - timing.commitAt);
                // The outbox applies the response synchronously after its outcome observer returns.
                const id = [...active].find(([, value]) => value === timing)?.[0];
                if (id !== undefined) options.later(() => { try { send(id, timing); } catch { /* Observer only. */ } }, 0);
            }
        },
        echo(operation: string, finished: boolean): void {
            const timing = operations.get(operation);
            if (!timing) return;
            if (!finished) timing.echoAt = options.now();
            else if (timing.echoAt !== undefined) { timing.echoMs += Math.max(0, options.now() - timing.echoAt); timing.echoAt = undefined; }
        },
        compile(entity: string): () => void {
            const started = options.now();
            return () => { try {
                const now = options.now(), ms = Math.max(0, now - started);
                // Host-job overlap, including an action that joined a compile already in flight; not an additive UI phase.
                for (const timing of active.values()) timing.compileMs += Math.max(0, now - Math.max(started, timing.started));
                if (ms > 2000) options.post({ kind: 'slow', reason: 'compile:' + entity, client_ms: Math.round(ms), detail: {} });
            } catch { /* Observer only. */ } };
        },
    };
    return api;
}
type ActionTiming = ReturnType<typeof createActionTiming>;
type TimingWindow = Window & { ftActionTiming?: ActionTiming };
function bridge(): ActionTiming | undefined {
    if (typeof window === 'undefined') return undefined;
    try { if (window.parent !== window && (window.parent as TimingWindow).ftActionTiming) return (window.parent as TimingWindow).ftActionTiming; } catch { /* Cross-origin frame. */ }
    return (window as TimingWindow).ftActionTiming;
}
export function startActionTiming(lane: ActionLane, id?: string): string | undefined {
    try { return bridge()?.start(lane, id); } catch { return undefined; }
}
/** Hold the click's start across async UI gates before a native lane is selected. */
export function beginActionTimingInput(): (() => void) | undefined {
    try {
        const finish = bridge()?.intent();
        return finish ? () => { try { finish(); } catch { /* Observer only. */ } } : undefined;
    } catch { return undefined; }
}
export function markActionTiming(id: string | undefined, phase: Phase): void {
    try { bridge()?.mark(id, phase); } catch { /* Observer only. */ }
}
export function bindActionTiming(id: string | undefined, operation: string): void {
    try { bridge()?.bind(id, operation); } catch { /* Observer only. */ }
}
export function endActionTiming(id: string | undefined, discard = false): void {
    try { bridge()?.end(id, discard); } catch { /* Observer only. */ }
}
export function echoActionTiming(operation: string, finished: boolean): void {
    try { bridge()?.echo(operation, finished); } catch { /* Observer only. */ }
}
export function withActionTimingEcho(command: unknown, apply: () => void): void {
    let operation: string | undefined;
    try {
        const id = (command as { operation_id?: unknown } | null)?.operation_id;
        if (typeof id === 'string') operation = id;
    } catch { /* A malformed frame must still reach its existing handler. */ }
    if (operation !== undefined) echoActionTiming(operation, false);
    try { apply(); } finally { if (operation !== undefined) echoActionTiming(operation, true); }
}
export function compileActionTiming(entity: string): (() => void) | undefined {
    try {
        const finish = bridge()?.compile(entity);
        return finish ? () => { try { finish(); } catch { /* Observer only. */ } } : undefined;
    } catch { return undefined; }
}

/** Capture the actual gesture before async page gates, including same-origin scene iframe clicks. */
export function installActionTimingInputs(doc: Document, timing: ActionTiming): void {
    const seen = new WeakSet<Document>();
    const attach = (target: Document): void => { try {
        if (seen.has(target)) return;
        seen.add(target);
        const input = (event: Event): void => { try {
            if (event.type === 'keydown' && !['Enter', ' ', 'Spacebar'].includes((event as KeyboardEvent).key)) return;
            timing.input();
        } catch { /* Observer only. */ } };
        for (const type of ['click', 'pointerup', 'submit', 'keydown']) target.addEventListener(type, input, true);
        const frame = (element: Element): void => { try {
            if (element.tagName === 'IFRAME') { const child = (element as HTMLIFrameElement).contentDocument; if (child) attach(child); }
        } catch { /* Cross-origin frame. */ } };
        for (const element of target.querySelectorAll('iframe')) frame(element);
        target.addEventListener('load', event => { try {
            if (event.target && 'tagName' in event.target) frame(event.target as Element);
        } catch { /* Observer only. */ } }, true);
    } catch { /* Unsupported document. */ } };
    attach(doc);
}
